import React, { useCallback, useState, useRef, useEffect, MutableRefObject } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { GetCard } from '@web/walter-lib/api'
import cn from 'classnames'

import { useTransaction } from '@web/walter-lib/stream'
import { TopUpViews } from './types'
import General from './views/General/General'
import Success from './views/Success/Success'
import Pending from './views/Pending/Pending'
import Error from './views/Error/Error'
import s from './s.module.css'
import Confirmation from '~components/ConfirmationModal/Confirmation'
import { usePromise } from '~hooks/usePromise'
import { cardsApi } from '~api'
import { processPermissionError } from '~utils/processPermissionError'
import { CardsViews } from '~pages/CardsPage/types'

interface Props {
    onClose: () => void
    view: string
    setView: (newView: TopUpViews) => void
    card?: GetCard
    opId: MutableRefObject<string>
    setPageView: (newView: CardsViews) => void
}

// eslint-disable-next-line max-lines-per-function,complexity
function Content({ onClose, view, setView, card, opId, setPageView }: Props) {
    const [confirmModal, setConfirmModal] = useState({ showTwoFa: false, showVerificationCode: false })
    const successDataRef = useRef({ from_currency: '', from_amount: '', id: '' })

    const { send: onTopUp, isLoading } = usePromise(async () => {
        try {
            const res = await cardsApi.postTopup({
                id: successDataRef.current.id,
                from_currency: successDataRef.current.from_currency,
                from_amount: successDataRef.current.from_amount,
                idempotency_key: uuidv4(),
            })

            setView(TopUpViews.pending)
            opId.current = res.operation_id
        } catch (error) {
            const { showVerificationCode, showTwoFa } = processPermissionError(error)

            if (showVerificationCode || showTwoFa) {
                setConfirmModal({ showTwoFa, showVerificationCode })
                setView(TopUpViews.confirm)
                return
            }

            throw error
        }
    })

    const onSuccessGeneral = useCallback(
        (from_currency: string, from_amount: string, id: string) => {
            successDataRef.current = { from_currency, from_amount, id }
            onTopUp()
        },
        [onTopUp]
    )

    const onSuccessConfirm = useCallback(() => {
        setView(TopUpViews.general)
        void onTopUp()
    }, [onTopUp, setView])

    const { data } = useTransaction()

    useEffect(() => {
        if (view === 'pending') {
            if (data?.type === 'WITHDRAW' && data.operation_id === opId.current) {
                if (data?.status === 'SUCCESS') {
                    setView(TopUpViews.success)
                } else if (data?.status === 'FAILED' || data?.status === 'ROLLED_BACK') {
                    setView(TopUpViews.error)
                }
            }
        }
    }, [data, opId, setView, view])

    return (
        <>
            <div className={cn(view !== 'general' && s.hide)}>
                <General onSuccess={onSuccessGeneral} isLoading={isLoading} initCard={card} setPageView={setPageView} />
            </div>
            {view === 'confirm' && (
                <Confirmation
                    initialInputState="pending"
                    onSuccess={onSuccessConfirm}
                    showTwoFa={confirmModal?.showTwoFa || false}
                    showVerificationCode={confirmModal?.showVerificationCode || false}
                    resource={cardsApi.resources.topup(successDataRef.current.id)}
                />
            )}
            {view === 'pending' && (
                <Pending
                    onClose={onClose}
                    data={{
                        currency: successDataRef.current.from_currency,
                        amount: successDataRef.current.from_amount,
                    }}
                />
            )}
            {view === 'success' && (
                <Success
                    onClose={onClose}
                    data={{
                        currency: successDataRef.current.from_currency,
                        amount: successDataRef.current.from_amount,
                    }}
                />
            )}
            {view === 'error' && <Error onClose={onClose} />}
        </>
    )
}

export default Content
